import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { passportApi } from "../lib/passport-api";

type Phase = "confirming" | "confirmed" | "error";

/**
 * Confirm an email change. POSTs the token on load (mirrors verify-email.tsx) — the change-email token is
 * single-use, so the swap of users.email happens exactly once, here. Public route: the link may be opened
 * from the new inbox in a different browser, with no session.
 */
export function EmailChangeConfirmPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [phase, setPhase] = useState<Phase>("confirming");
  // Single-use token + StrictMode double-invoke → fire once (same guard rationale as verify-email.tsx).
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setPhase("error");
      return;
    }
    (async () => {
      try {
        await passportApi.auth.confirmEmailChange(token);
        setPhase("confirmed");
      } catch {
        setPhase("error");
      }
    })();
  }, [token]);

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <Card>
          {phase === "confirming" && (
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-semibold">Updating your email…</CardTitle>
              <CardDescription>One moment.</CardDescription>
            </CardHeader>
          )}
          {phase === "confirmed" && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-semibold">Email updated</CardTitle>
                <CardDescription>
                  Your email address has been changed. Use the new address next time you sign in.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/login" className="text-sm text-foreground underline-offset-4 hover:underline">
                  Continue to sign in
                </Link>
              </CardContent>
            </>
          )}
          {phase === "error" && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-semibold">Couldn't update email</CardTitle>
                <CardDescription>
                  This confirmation link is missing, invalid, expired, already used, or the address is now taken.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/account" className="text-sm text-foreground underline-offset-4 hover:underline">
                  Back to your account
                </Link>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
